import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import { embedDashboard } from '@superset-ui/embedded-sdk'
import './DashboardContainer.css'

const SUPERSET_DOMAIN = 'https://superset.voyagergm.io'
// Embed UUID of the dashboard enabled for embedding in Superset (⋯ → Embed dashboard).
const EMBED_UUID = 'e5dd5dab-bdb7-486d-99ce-e2031819ba34'

const GenerateUserSupersetDashboardToken = gql`
    mutation generateUserSupersetDashboardToken($input: GenerateUserJwtTokenInput!) {
        generateUserSupersetDashboardToken(input: $input) {
            ok
            token
            errors { messages field }
        }
    }
`

export default function DashboardContainer({ currentUser }) {
    const mountRef = useRef(null)
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const [generateToken] = useMutation(GenerateUserSupersetDashboardToken)

    // Mints a fresh guest token from Django. The SDK calls this on load and again
    // whenever the current token nears expiry, so it must always resolve to a token.
    const fetchGuestToken = useCallback(async () => {
        const { data } = await generateToken({
            variables: { input: { userId: currentUser?.id } },
        })
        const result = data?.generateUserSupersetDashboardToken
        if (!result?.ok || !result?.token) {
            const message = result?.errors?.[0]?.messages?.[0] || 'Failed to load dashboard token'
            throw new Error(message)
        }
        return result.token
    }, [generateToken, currentUser])

    useEffect(() => {
        if (!mountRef.current) return

        let unmount
        embedDashboard({
            id: EMBED_UUID,
            supersetDomain: SUPERSET_DOMAIN,
            mountPoint: mountRef.current,
            fetchGuestToken,
            dashboardUiConfig: { hideTitle: true, filters: { expanded: false } },
        })
            .then((instance) => {
                unmount = instance?.unmount
                setLoading(false)
            })
            .catch((err) => {
                console.error('Failed to embed Superset dashboard:', err)
                setError(err.message || 'Failed to load dashboard')
                setLoading(false)
            })

        return () => {
            if (typeof unmount === 'function') unmount()
        }
    }, [fetchGuestToken])

    return (
        <div className="dashboard-page">
            {error && <div className="alert alert-danger">{error}</div>}
            {loading && !error && <div className="dashboard-loading">Loading dashboard…</div>}
            <div ref={mountRef} className="dashboard-embed" />
        </div>
    )
}
